import { useState, useCallback } from 'react';

/**
 * ズーム操作の定数
 */
const ZOOM_CONFIG = {
  DEFAULT: 100,  // デフォルトズームレベル（%）
  MIN: 50,       // 最小ズームレベル（%）
  MAX: 200,      // 最大ズームレベル（%）
  STEP: 10,      // ズームの増減単位（%）
};

/**
 * ズーム操作を管理するカスタムフック
 * 
 * PDFビューアのズームイン・ズームアウト操作を提供する。
 * 
 * @param {number} initialZoom - 初期ズームレベル（デフォルト: 100）
 * @returns {Object} ズーム関連の状態とハンドラ
 * @returns {number} returns.zoomLevel - 現在のズームレベル（50-200%）
 * @returns {Function} returns.handleZoomIn - ズームインハンドラ（10%単位、最大200%）
 * @returns {Function} returns.handleZoomOut - ズームアウトハンドラ（10%単位、最小50%）
 * @returns {Function} returns.resetZoom - ズームをデフォルト（100%）にリセット
 */
export const useZoomControls = (initialZoom = ZOOM_CONFIG.DEFAULT) => {
  /**
   * 現在のズームレベル（パーセント）
   * 範囲: 50% - 200%
   */
  const [zoomLevel, setZoomLevel] = useState(initialZoom);

  /**
   * ズームイン（10%単位、最大200%）
   */
  const handleZoomIn = useCallback(() => {
    setZoomLevel((prev) => Math.min(prev + ZOOM_CONFIG.STEP, ZOOM_CONFIG.MAX));
  }, []);

  /**
   * ズームアウト（10%単位、最小50%）
   */
  const handleZoomOut = useCallback(() => {
    setZoomLevel((prev) => Math.max(prev - ZOOM_CONFIG.STEP, ZOOM_CONFIG.MIN));
  }, []);

  /**
   * ズームをデフォルト値にリセット
   */
  const resetZoom = useCallback(() => {
    setZoomLevel(ZOOM_CONFIG.DEFAULT);
  }, []);

  return {
    zoomLevel,
    handleZoomIn,
    handleZoomOut,
    resetZoom,
  };
};

export default useZoomControls;
